// frontend/src/components/projects/workspaces/ScienceProjectWorkspace.tsx
// Fen ve Teknoloji dersleri için Bilimsel Araştırma ve Deney Tasarım Laboratuvarı

import { useState, useEffect } from 'react';

export interface ScienceWorkData {
  hypothesis: string;
  independentVar: string;
  dependentVar: string;
  controlledVars: string;
  steps: string[];
  observations: string;
  conclusion: string;
}

interface ScienceProjectWorkspaceProps {
  initialContent: string;
  projectTitle: string;
  projectDescription: string | null;
  onChange: (formattedText: string) => void;
  disabled?: boolean;
}

export function ScienceProjectWorkspace({
  initialContent,
  projectTitle,
  projectDescription,
  onChange,
  disabled = false,
}: ScienceProjectWorkspaceProps) {
  const [data, setData] = useState<ScienceWorkData>(() => {
    try {
      if (initialContent.startsWith('{') && initialContent.includes('hypothesis')) {
        return JSON.parse(initialContent);
      }
    } catch {
      // Fallback: parse text
    }
    return {
      hypothesis: '',
      independentVar: '',
      dependentVar: '',
      controlledVars: '',
      steps: ['1. Gözlem ve hazırlık aşamasını tamamla.', '2. Ölçüm değerlerini kaydet.'],
      observations: '',
      conclusion: '',
    };
  });

  const [activeTab, setActiveTab] = useState<'editor' | 'report'>('editor');

  // Her veri değişiminde dışarıya hem yapılandırılmış metin hem JSON aktarılır
  useEffect(() => {
    const formatted = `### 🧪 BİLİMSEL DENEY VE ARAŞTIRMA RAPORU
**Proje:** ${projectTitle}

#### 1. Hipotez & Araştırma Sorusu
${data.hypothesis || '(Henüz girilmedi)'}

#### 2. Deney Değişkenleri
- **Bağımsız Değişken:** ${data.independentVar || '-'}
- **Bağımlı Değişken:** ${data.dependentVar || '-'}
- **Kontrol Altında Tutulan Değişkenler:** ${data.controlledVars || '-'}

#### 3. Deney & Simülasyon Aşamaları
${data.steps.map((s, i) => `${i + 1}. ${s}`).join('\n')}

#### 4. Gözlem Bulguları & Ölçümler
${data.observations || '(Gözlem verisi yok)'}

#### 5. Bilimsel Çıkarım & Sonuç
${data.conclusion || '(Sonuç belirtilmedi)'}
`;
    onChange(formatted);
  }, [data, projectTitle, onChange]);

  function updateField<K extends keyof ScienceWorkData>(key: K, value: ScienceWorkData[K]) {
    setData((prev) => ({ ...prev, [key]: value }));
  }

  function handleStepChange(index: number, val: string) {
    const newSteps = [...data.steps];
    newSteps[index] = val;
    updateField('steps', newSteps);
  }

  function addStep() {
    updateField('steps', [...data.steps, '']);
  }

  function removeStep(index: number) {
    if (data.steps.length <= 1) return;
    updateField('steps', data.steps.filter((_, i) => i !== index));
  }

  return (
    <div className="science-workspace-container">
      {/* Sol Panel: İnteraktif Bilimsel Rapor Formu */}
      <div className="science-editor-panel card">
        <div className="science-panel-header">
          <div className="science-badge">🧪 Bilimsel Deney & Araştırma Laboratuvarı</div>
          <div className="science-tabs">
            <button
              type="button"
              className={`science-tab-btn ${activeTab === 'editor' ? 'active' : ''}`}
              onClick={() => setActiveTab('editor')}
            >
              📝 Deney Tasarımı
            </button>
            <button
              type="button"
              className={`science-tab-btn ${activeTab === 'report' ? 'active' : ''}`}
              onClick={() => setActiveTab('report')}
            >
              📋 Rapor Önizleme
            </button>
          </div>
        </div>

        {activeTab === 'editor' ? (
          <div className="science-form-body">
            {/* 1. Hipotez */}
            <div className="science-form-group">
              <label className="science-label">
                <span>1. Hipotez & Araştırma Sorusu</span>
                <span className="science-hint">Örn: "Güneş'e olan uzaklık arttıkça gezegenin dolanma süresi uzar çünkü..."</span>
              </label>
              <textarea
                className="input science-textarea"
                rows={3}
                placeholder="Test etmek istediğin bilimsel hipotezini veya araştırma sorunu açıkla..."
                value={data.hypothesis}
                onChange={(e) => updateField('hypothesis', e.target.value)}
                disabled={disabled}
              />
            </div>

            {/* 2. Değişkenler */}
            <div className="science-form-group">
              <label className="science-label">
                <span>2. Deney Değişkenleri & Parametreler</span>
              </label>
              <div className="science-grid-3">
                <div>
                  <div className="science-sublabel">🔵 Bağımsız Değişken (Değiştirdiğin)</div>
                  <input
                    type="text"
                    className="input"
                    placeholder="Örn: Işık şiddeti, Mesafe..."
                    value={data.independentVar}
                    onChange={(e) => updateField('independentVar', e.target.value)}
                    disabled={disabled}
                  />
                </div>
                <div>
                  <div className="science-sublabel">🟢 Bağımlı Değişken (Ölçtüğün)</div>
                  <input
                    type="text"
                    className="input"
                    placeholder="Örn: Büyüme hızı, Sıcaklık..."
                    value={data.dependentVar}
                    onChange={(e) => updateField('dependentVar', e.target.value)}
                    disabled={disabled}
                  />
                </div>
                <div>
                  <div className="science-sublabel">⚪ Sabit Değişkenler (Kontrol)</div>
                  <input
                    type="text"
                    className="input"
                    placeholder="Örn: Ortam sıcaklığı, Kütle..."
                    value={data.controlledVars}
                    onChange={(e) => updateField('controlledVars', e.target.value)}
                    disabled={disabled}
                  />
                </div>
              </div>
            </div>

            {/* 3. Deney Aşamaları */}
            <div className="science-form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label className="science-label" style={{ margin: 0 }}>
                  <span>3. Deney & Gözlem Protokolü (Aşamalar)</span>
                </label>
                <button type="button" className="science-add-btn" onClick={addStep} disabled={disabled}>
                  + Adım Ekle
                </button>
              </div>
              <div className="science-steps-list">
                {data.steps.map((step, idx) => (
                  <div key={idx} className="science-step-row">
                    <span className="science-step-num">{idx + 1}</span>
                    <input
                      type="text"
                      className="input"
                      style={{ flex: 1 }}
                      placeholder={`${idx + 1}. adımda yapılacak işlem veya simülasyon gözlemi...`}
                      value={step}
                      onChange={(e) => handleStepChange(idx, e.target.value)}
                      disabled={disabled}
                    />
                    {data.steps.length > 1 && (
                      <button
                        type="button"
                        className="science-del-btn"
                        onClick={() => removeStep(idx)}
                        disabled={disabled}
                        title="Adımı Sil"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* 4. Bulgular & Veriler */}
            <div className="science-form-group">
              <label className="science-label">
                <span>4. Gözlem Bulguları, Veriler & Ölçümler</span>
                <span className="science-hint">Deney esnasında gözlemlediğin olaylar, ölçülen değerler ve veriler</span>
              </label>
              <textarea
                className="input science-textarea"
                rows={3}
                placeholder="Örn: 1. dakikada 5 kabarcık gözlendi, mesafe 2 katına çıktığında dolanma hızı %40 azaldı..."
                value={data.observations}
                onChange={(e) => updateField('observations', e.target.value)}
                disabled={disabled}
              />
            </div>

            {/* 5. Bilimsel Çıkarım */}
            <div className="science-form-group">
              <label className="science-label">
                <span>5. Bilimsel Çıkarım & Sonuç Analizi</span>
                <span className="science-hint">Elde ettiğin veriler hipotezini destekledi mi? Bilimsel kanıtlarla açıkla.</span>
              </label>
              <textarea
                className="input science-textarea"
                rows={3}
                placeholder="Örn: Deney verileri hipotezimizi doğrulamıştır. Çünkü..."
                value={data.conclusion}
                onChange={(e) => updateField('conclusion', e.target.value)}
                disabled={disabled}
              />
            </div>
          </div>
        ) : (
          <div className="science-report-view">
            <div className="science-report-header">
              <h2>📄 Bilimsel Deney Raporu</h2>
              <div className="science-report-title">{projectTitle}</div>
            </div>
            <div className="science-report-section">
              <h4>🎯 Hipotez</h4>
              <p>{data.hypothesis || '— Hipotez henüz yazılmadı —'}</p>
            </div>
            <div className="science-report-section">
              <h4>🔍 Değişken Analizi</h4>
              <ul>
                <li><strong>Bağımsız Değişken:</strong> {data.independentVar || 'Belirtilmedi'}</li>
                <li><strong>Bağımlı Değişken:</strong> {data.dependentVar || 'Belirtilmedi'}</li>
                <li><strong>Kontrol Değişkenleri:</strong> {data.controlledVars || 'Belirtilmedi'}</li>
              </ul>
            </div>
            <div className="science-report-section">
              <h4>📋 Uygulama Protokolü</h4>
              <ol>
                {data.steps.map((st, i) => (
                  <li key={i}>{st || 'Boş adım'}</li>
                ))}
              </ol>
            </div>
            <div className="science-report-section">
              <h4>📊 Ölçüm & Gözlemler</h4>
              <p>{data.observations || '— Gözlem verisi girilmedi —'}</p>
            </div>
            <div className="science-report-section">
              <h4>🔬 Bilimsel Çıkarım</h4>
              <p>{data.conclusion || '— Çıkarım henüz yazılmadı —'}</p>
            </div>
          </div>
        )}
      </div>

      {/* Sağ Panel: Canlı Bilim Laboratuvarı & Metodoloji Rehberi */}
      <div className="science-side-panel">
        <div className="card science-guide-card">
          <h3>🔬 Bilimsel Yöntem Rehberi</h3>
          <p className="science-guide-desc">
            Fen projelerinde bilimsel araştırma basamaklarını doğru uygulamak yüksek değerlendirme puanı kazandırır.
          </p>
          <div className="science-checklist">
            <div className={`science-check-item ${data.hypothesis.trim() ? 'done' : ''}`}>
              <span className="check-icon">{data.hypothesis.trim() ? '✅' : '⚪'}</span>
              <div>
                <strong>Net Bir Hipotez Kur</strong>
                <p>Neden-sonuç ilişkisi içeren doğrulanabilir bir cümle yaz.</p>
              </div>
            </div>
            <div className={`science-check-item ${data.independentVar.trim() && data.dependentVar.trim() ? 'done' : ''}`}>
              <span className="check-icon">{data.independentVar.trim() && data.dependentVar.trim() ? '✅' : '⚪'}</span>
              <div>
                <strong>Değişkenleri Ayrıştır</strong>
                <p>Neyi değiştirdiğini ve neyi ölçtüğünü netleştir.</p>
              </div>
            </div>
            <div className={`science-check-item ${data.observations.trim() ? 'done' : ''}`}>
              <span className="check-icon">{data.observations.trim() ? '✅' : '⚪'}</span>
              <div>
                <strong>Veri ve Ölçümleri Kaydet</strong>
                <p>Sayısal veriler, karşılaştırmalar veya gözlem sonuçları ekle.</p>
              </div>
            </div>
            <div className={`science-check-item ${data.conclusion.trim() ? 'done' : ''}`}>
              <span className="check-icon">{data.conclusion.trim() ? '✅' : '⚪'}</span>
              <div>
                <strong>Bilimsel Çıkarımını Yaz</strong>
                <p>Bulguların teorik fen bilgileriyle uyumunu açıkla.</p>
              </div>
            </div>
          </div>
        </div>

        {projectDescription && (
          <div className="card science-info-card">
            <h4>📌 Görev Hedefi</h4>
            <p>{projectDescription}</p>
          </div>
        )}
      </div>
    </div>
  );
}
